/*
 * Add to cart without leaving the product page: Ajax add + cart drawer +
 * "Complete the kit".
 *
 * Loaded only when sections/product.liquid renders snippets/cart-drawer.liquid
 * (section setting `cart_drawer`). Contract with the page:
 *   form#ProductForm              Shopify's product form (hidden `id`, `quantity`)
 *   .product-form__add-btn        its submit button (state owned by variant-picker.js)
 *   [data-product-variants]       the variants JSON variant-picker.js reads
 *   [data-cart-drawer]            the drawer markup + kit parts JSON
 *   .header__cart-btn             header bag link; its count badge is kept live
 *
 * NEVER ADD TWICE. The native form post is the fallback ONLY when it is certain
 * nothing was added:
 *   - the pre-add GET /cart.js cannot be made (nothing has been sent yet) →
 *     native post;
 *   - the add request itself gets no response (network drop) → re-read
 *     /cart.js; if the line grew, the add DID land and the drawer opens;
 *     if it did not, native post; if the cart cannot be read either, send the
 *     shopper to /cart rather than risk posting the item again;
 *   - Shopify answers with an error (sold out, over stock) → its message under
 *     the button, nothing else;
 *   - anything that goes wrong AFTER a successful add (rendering, a failed
 *     /cart.js refresh) degrades the drawer, and never submits the form.
 *
 * PER-LINE CAP. The quantity input's max is the theme's per-line cap (3, or
 * stock when lower). The native form only caps one submission; staying on the
 * page would let a shopper add 3, then 3 more. So the quantity already on the
 * line (from the pre-add /cart.js) is subtracted first.
 *
 * "Buy it now" (Shopify's dynamic checkout button) is not a form submit and is
 * left alone.
 *
 * T23 — THE DRAWER IS THE CART. It lists every line of /cart.js (not only the
 * item just added), each with a − / + quantity stepper and Remove, via
 * /cart/change.js keyed by the line `key`. One change runs at a time (every
 * line control is disabled while it is out), the response IS the new cart, so
 * lines, subtotal, item count and the header badge all re-render from it. A
 * failed change re-reads /cart.js so the drawer never shows a quantity the
 * cart does not have. − stops at 1 (Remove removes, the /cart page rule) and
 * + stops at the per-line cap. Focus returns to the same control on the same
 * line after the re-render; the polite status line announces the result.
 *
 * STALE RESPONSES. Several requests can be out at once (a "Complete the kit"
 * add re-reads /cart.js while the shopper presses +). Every request whose
 * answer is a cart takes a sequence number when it is SENT, and showCart()
 * drops a cart older than the one already on screen, so a slow early reply
 * can never put back a quantity the shopper has since changed. Send order is
 * the right order: Shopify applies cart writes in the order it receives them,
 * so a later request's cart already includes every earlier change.
 */
(function () {
  'use strict';

  var KIT_SHOWN = 4;
  var DEFAULT_CAP = 3;

  function rootUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + path;
  }

  // Resolves {ok, status, body} for any HTTP response (body null when it is
  // not JSON); REJECTS only when no response arrived at all.
  function request(path, options) {
    return fetch(rootUrl(path), Object.assign({ headers: { Accept: 'application/json' } }, options || {})).then(function (res) {
      return res.text().then(function (text) {
        var body = null;
        try { body = text ? JSON.parse(text) : null; } catch (e) { body = null; }
        return { ok: res.ok, status: res.status, body: body };
      });
    });
  }

  function getCart() {
    return request('/cart.js').then(function (r) {
      return r.ok && r.body && typeof r.body.item_count === 'number' ? r.body : null;
    });
  }

  function formatMoney(cents, currency) {
    var amount = Number(cents) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  // Shopify CDN images accept a width parameter; the API hands back originals.
  function sized(src, width) {
    if (!src) return '';
    if (/[?&]width=\d+/.test(src)) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function lineQuantity(cart, variantId) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (sum, item) {
      return item.variant_id === variantId ? sum + item.quantity : sum;
    }, 0);
  }

  function init() {
    var form = document.getElementById('ProductForm');
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!form || !drawer || drawer.dataset.cartDrawerReady) return;

    // Theme editor re-render: a previous drawer already lives on <body>.
    document.querySelectorAll('[data-cart-drawer]').forEach(function (old) {
      if (old !== drawer && old.parentElement === document.body) old.remove();
    });
    drawer.dataset.cartDrawerReady = '1';
    // Out of the section's stacking context (the sticky header sits above it).
    document.body.appendChild(drawer);

    var panel = drawer.querySelector('[data-cart-drawer-panel]');
    var addedEl = drawer.querySelector('[data-cart-drawer-added]');
    var statusEl = drawer.querySelector('[data-cart-drawer-status]');
    var kitEl = drawer.querySelector('[data-cart-drawer-kit]');
    var kitList = drawer.querySelector('[data-cart-drawer-kit-list]');
    var countEl = drawer.querySelector('[data-cart-drawer-count]');
    var subtotalEl = drawer.querySelector('[data-cart-drawer-subtotal]');
    var subtotalRow = subtotalEl ? subtotalEl.closest('.cart-drawer__subtotal') : null;
    var titleEl = drawer.querySelector('#CartDrawerTitle');
    var footEl = drawer.querySelector('.cart-drawer__foot');
    var addBtn = form.querySelector('.product-form__add-btn');
    var qtyInput = form.querySelector('input[name="quantity"]');
    var productId = Number(drawer.dataset.productId);
    var cartUrl = drawer.dataset.cartUrl || '/cart';
    var busy = false;
    var hideTimer = null;
    // Stale-response guard (see the header): numbered at send, newest shown wins.
    var cartSeq = 0;
    var shownSeq = 0;
    var inerted = [];

    var parts = [];
    try {
      var raw = drawer.querySelector('[data-cart-drawer-parts]');
      parts = raw ? JSON.parse(raw.textContent || '[]') : [];
    } catch (e) {
      parts = [];
    }

    var variants = [];
    try {
      var vraw = form.querySelector('[data-product-variants]');
      variants = vraw ? JSON.parse(vraw.textContent || '[]') : [];
    } catch (e) {
      variants = [];
    }

    // Complementary products (Search & Discovery), fetched once, lazily.
    var complementary = null;
    function loadComplementary() {
      if (complementary) return complementary;
      complementary = request('/recommendations/products.json?product_id=' + productId + '&intent=complementary&limit=4')
        .then(function (r) {
          var list = (r.ok && r.body && r.body.products) || [];
          return list
            .filter(function (p) { return p && p.available && p.variants && p.variants.length; })
            .map(function (p) {
              var v = p.variants.filter(function (x) { return x.available; })[0] || p.variants[0];
              return {
                product_id: p.id,
                variant_id: v.id,
                title: p.title,
                url: p.url,
                image: p.featured_image ? sized(p.featured_image, 160) : null,
                price: v.price,
                single_variant: p.variants.length === 1,
              };
            });
        })
        .catch(function () { return []; });
      return complementary;
    }

    // --- add button state ---------------------------------------------------
    // Rebuilt from the variants JSON after a request, instead of restoring a
    // saved label: the shopper may have switched to a sold-out size or an
    // unavailable combination while the request was out.
    function syncButton() {
      if (!addBtn) return;
      var idInput = form.querySelector('input[name="id"]');
      var id = idInput ? Number(idInput.value) : 0;
      var label;
      var available;
      if (!id) {
        label = 'Unavailable';
        available = false;
      } else {
        var match = variants.filter(function (v) { return v.id === id; })[0];
        available = match ? !!match.available : true;
        label = available ? 'Add to cart' : 'Sold out';
      }
      addBtn.removeAttribute('aria-busy');
      addBtn.disabled = !available;
      addBtn.textContent = label;
      addBtn.setAttribute('aria-label', label);
    }

    // --- header badge -----------------------------------------------------
    function updateHeaderCount(count) {
      document.querySelectorAll('.header__cart-btn').forEach(function (link) {
        link.setAttribute('aria-label', 'Cart (' + count + ' items)');
        var icon = link.querySelector('.header__action-icon') || link;
        var badge = link.querySelector('.header__cart-count');
        if (count > 0) {
          if (!badge) {
            badge = el('span', 'header__cart-count');
            badge.setAttribute('aria-live', 'polite');
            icon.appendChild(badge);
          }
          badge.textContent = String(count);
        } else if (badge) {
          badge.remove();
        }
      });
    }

    // `seq` is the number the request took when it was sent; a cart read by
    // readCart()/refreshCart() carries it as cart.__seq. Returns false when the
    // cart was older than what is already shown and was ignored.
    function showCart(cart, seq) {
      var s = seq != null ? seq : cart && cart.__seq != null ? cart.__seq : null;
      if (s != null) {
        if (s < shownSeq) return false;
        shownSeq = s;
      }
      if (cart) {
        countEl.textContent = cart.item_count === 1 ? '1 item' : cart.item_count + ' items';
        subtotalEl.textContent = formatMoney(cart.total_price, cart.currency);
        if (subtotalRow) subtotalRow.hidden = false;
        // T23: nothing to check out → no subtotal / Checkout row.
        if (footEl) footEl.hidden = cart.item_count === 0;
        updateHeaderCount(cart.item_count);
        renderLines(cart);
      } else if (subtotalRow) {
        // The add landed but the cart could not be re-read: no half-filled
        // "Subtotal (0)"; View cart still shows the truth.
        subtotalRow.hidden = true;
      }
      return true;
    }

    // GET /cart.js, numbered at send time.
    function readCart() {
      var seq = ++cartSeq;
      return getCart().then(function (cart) {
        if (cart) cart.__seq = seq;
        return cart;
      });
    }

    function refreshCart() {
      var seq = ++cartSeq;
      return getCart().catch(function () { return null; }).then(function (cart) {
        if (cart) cart.__seq = seq;
        showCart(cart, seq);
        return cart;
      });
    }

    // --- drawer open/close + focus trap -----------------------------------
    function focusables() {
      return Array.prototype.slice.call(
        panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter(function (n) { return !n.closest('[hidden]'); });
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) { e.preventDefault(); panel.focus(); return; }
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      var inside = panel.contains(active) && active !== panel;
      if (e.shiftKey && (!inside || active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    }

    function setBackgroundInert(on) {
      if (on) {
        inerted = Array.prototype.filter.call(document.body.children, function (child) {
          return child !== drawer && !child.hasAttribute('inert') && child.tagName !== 'SCRIPT';
        });
        inerted.forEach(function (child) { child.setAttribute('inert', ''); });
      } else {
        inerted.forEach(function (child) { child.removeAttribute('inert'); });
        inerted = [];
      }
    }

    function open() {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      drawer.hidden = false;
      void panel.offsetWidth; // start the transition from the closed position
      drawer.classList.add('is-open');
      document.documentElement.classList.add('cart-drawer-open');
      setBackgroundInert(true);
      panel.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      drawer.classList.remove('is-open');
      document.documentElement.classList.remove('cart-drawer-open');
      setBackgroundInert(false);
      document.removeEventListener('keydown', onKeydown);
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      hideTimer = setTimeout(function () { drawer.hidden = true; hideTimer = null; }, reduce ? 0 : 250);
      // The add button was disabled while the request ran, so the element that
      // "had focus" is usually <body>; the button is the useful place to land.
      if (addBtn && !addBtn.disabled) addBtn.focus();
    }

    drawer.querySelectorAll('[data-cart-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    // --- rendering ----------------------------------------------------------
    function renderAdded(line, quantityAdded, currency) {
      addedEl.innerHTML = '';
      var row = el('div', 'cart-drawer__line');
      if (line.image) {
        var img = el('img', 'cart-drawer__line-img');
        img.src = sized(line.image, 160);
        img.alt = '';
        img.width = 80;
        img.height = 80;
        row.appendChild(img);
      }
      var text = el('div', 'cart-drawer__line-text');
      text.appendChild(el('p', 'cart-drawer__line-title', line.product_title || line.title || ''));
      if (line.variant_title && !line.product_has_only_default_variant) {
        text.appendChild(el('p', 'cart-drawer__line-variant label-mono', line.variant_title));
      }
      // add.js reports the line's MERGED quantity; show what was just added.
      var qty = quantityAdded > 1 ? quantityAdded + ' × ' : '';
      if (line.final_price != null) {
        text.appendChild(el('p', 'cart-drawer__line-price', qty + formatMoney(line.final_price, currency)));
      }
      row.appendChild(text);
      addedEl.appendChild(row);
    }

    // --- T23: every cart line, with quantity + remove ------------------------
    var lineBusy = false;

    function lineLabel(item) {
      var title = item.product_title || item.title || 'item';
      return item.variant_title && !item.product_has_only_default_variant ? title + ' (' + item.variant_title + ')' : title;
    }

    function lineButton(className, action, text, label, disabled) {
      var b = el('button', className, text);
      b.type = 'button';
      b.setAttribute('data-cart-line-action', action);
      b.setAttribute('aria-label', label);
      b.disabled = !!disabled || lineBusy;
      return b;
    }

    function renderLines(cart) {
      var items = (cart && cart.items) || [];
      addedEl.innerHTML = '';
      if (!items.length) {
        addedEl.appendChild(el('p', 'cart-drawer__empty', 'Your cart is empty.'));
        return;
      }
      var list = el('ul', 'cart-drawer__lines');
      list.setAttribute('role', 'list');
      items.forEach(function (item) {
        var label = lineLabel(item);
        var li = el('li', 'cart-drawer__line');
        li.setAttribute('data-line-key', item.key);
        if (item.image) {
          var img = el('img', 'cart-drawer__line-img');
          img.src = sized(item.image, 160);
          img.alt = '';
          img.width = 80;
          img.height = 80;
          li.appendChild(img);
        }
        var text = el('div', 'cart-drawer__line-text');
        var titleP = el('p', 'cart-drawer__line-title');
        var titleLink = el('a', 'cart-drawer__line-link', item.product_title || item.title || '');
        titleLink.href = item.url || '#';
        titleP.appendChild(titleLink);
        text.appendChild(titleP);
        if (item.variant_title && !item.product_has_only_default_variant) {
          text.appendChild(el('p', 'cart-drawer__line-variant label-mono', item.variant_title));
        }
        if (item.final_line_price != null) {
          text.appendChild(el('p', 'cart-drawer__line-price', formatMoney(item.final_line_price, cart.currency)));
        }

        var controls = el('div', 'cart-drawer__line-controls');
        var qty = el('div', 'cart-drawer__qty');
        qty.setAttribute('role', 'group');
        qty.setAttribute('aria-label', 'Quantity, ' + label);
        qty.appendChild(lineButton('cart-drawer__qty-btn', 'decrease', '−', 'Decrease quantity of ' + label, item.quantity <= 1));
        var value = el('span', 'cart-drawer__qty-value', String(item.quantity));
        qty.appendChild(value);
        qty.appendChild(lineButton('cart-drawer__qty-btn', 'increase', '+', 'Increase quantity of ' + label, item.quantity >= DEFAULT_CAP));
        controls.appendChild(qty);
        controls.appendChild(lineButton('cart-drawer__remove', 'remove', 'Remove', 'Remove ' + label + ' from cart', false));
        text.appendChild(controls);

        li.appendChild(text);
        list.appendChild(li);
      });
      addedEl.appendChild(list);
    }

    function setLinesBusy(on) {
      lineBusy = on;
      var list = addedEl.querySelector('.cart-drawer__lines');
      if (list) {
        if (on) list.setAttribute('aria-busy', 'true');
        else list.removeAttribute('aria-busy');
      }
      addedEl.querySelectorAll('[data-cart-line-action]').forEach(function (b) {
        if (on) {
          b.disabled = true;
          return;
        }
        // Restore from the rendered quantity, never blindly re-enable.
        var line = b.closest('.cart-drawer__line');
        var q = line ? parseInt((line.querySelector('.cart-drawer__qty-value') || {}).textContent, 10) || 1 : 1;
        var action = b.getAttribute('data-cart-line-action');
        b.disabled = (action === 'decrease' && q <= 1) || (action === 'increase' && q >= DEFAULT_CAP);
      });
    }

    function focusLineControl(key, action) {
      var lines = addedEl.querySelectorAll('.cart-drawer__line');
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].getAttribute('data-line-key') !== key) continue;
        var target = lines[i].querySelector('[data-cart-line-action="' + action + '"]');
        if (!target || target.disabled) target = lines[i].querySelector('[data-cart-line-action]:not([disabled])');
        if (target) { target.focus(); return; }
      }
      // The line is gone (removed): keep focus inside the dialog.
      panel.focus();
    }

    function changeLine(key, quantity, label, action) {
      if (lineBusy) return;
      setLinesBusy(true);
      if (titleEl) titleEl.textContent = 'Your cart';
      statusEl.textContent = '';
      var seq = ++cartSeq;
      request('/cart/change.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then(
          function (r) {
            if (r.ok && r.body && typeof r.body.item_count === 'number') {
              lineBusy = false;
              r.body.__seq = seq;
              showCart(r.body);
              statusEl.textContent = quantity === 0 ? label + ' removed from your cart.' : 'Quantity of ' + label + ' is now ' + quantity + '.';
              return;
            }
            // Shopify said no (e.g. more than the stock): show why, then show
            // the cart as it really is.
            var message = (r.body && (r.body.description || r.body.message)) || 'Your cart could not be updated. Please try again.';
            lineBusy = false;
            return refreshCart().then(function () { statusEl.textContent = message; });
          },
          function () {
            lineBusy = false;
            return refreshCart().then(function () { statusEl.textContent = 'Your cart could not be updated. Please try again.'; });
          }
        )
        .catch(function () { /* rendering problems must not leave the drawer locked */ })
        .then(function () {
          setLinesBusy(false);
          focusLineControl(key, action);
        });
    }

    addedEl.addEventListener('click', function (e) {
      var button = e.target && e.target.closest ? e.target.closest('[data-cart-line-action]') : null;
      if (!button || button.disabled || lineBusy) return;
      var line = button.closest('.cart-drawer__line');
      if (!line) return;
      var key = line.getAttribute('data-line-key');
      var current = parseInt((line.querySelector('.cart-drawer__qty-value') || {}).textContent, 10) || 1;
      var action = button.getAttribute('data-cart-line-action');
      var group = line.querySelector('.cart-drawer__qty');
      var label = group ? (group.getAttribute('aria-label') || '').replace(/^Quantity, /, '') : 'item';
      var next = action === 'remove' ? 0 : action === 'increase' ? Math.min(current + 1, DEFAULT_CAP) : Math.max(current - 1, 1);
      if (action !== 'remove' && next === current) return;
      changeLine(key, next, label, action);
    });

    function renderKit(items, currency) {
      kitList.innerHTML = '';
      if (!items.length) {
        kitEl.hidden = true;
        return;
      }
      items.forEach(function (item) {
        var li = el('li', 'cart-drawer__kit-item');
        var link = el('a', 'cart-drawer__kit-link');
        link.href = item.url;
        if (item.image) {
          var img = el('img', 'cart-drawer__kit-img');
          img.src = item.image;
          img.alt = '';
          img.width = 56;
          img.height = 56;
          img.loading = 'lazy';
          link.appendChild(img);
        }
        var text = el('span', 'cart-drawer__kit-text');
        text.appendChild(el('span', 'cart-drawer__kit-name', item.title));
        text.appendChild(el('span', 'cart-drawer__kit-price', formatMoney(item.price, currency)));
        link.appendChild(text);
        li.appendChild(link);

        if (item.single_variant) {
          var add = el('button', 'btn cart-drawer__kit-add', 'Add');
          add.type = 'button';
          add.setAttribute('aria-label', 'Add ' + item.title + ' to cart');
          add.addEventListener('click', function () { addKitItem(item, add, currency); });
          li.appendChild(add);
        } else {
          // Never add a default variant silently: tint / size must be chosen.
          var choose = el('a', 'btn cart-drawer__kit-add cart-drawer__kit-add--choose', 'Choose');
          choose.href = item.url;
          choose.setAttribute('aria-label', 'Choose options for ' + item.title);
          li.appendChild(choose);
        }
        kitList.appendChild(li);
      });
      kitEl.hidden = false;
    }

    function kitItems(cart) {
      return loadComplementary().then(function (comp) {
        var seen = {};
        seen[productId] = 1;
        // Not already in the cart.
        if (cart && cart.items) cart.items.forEach(function (i) { seen[i.product_id] = 1; });
        var out = [];
        parts.concat(comp).forEach(function (item) {
          if (out.length >= KIT_SHOWN) return;
          if (!item || seen[item.product_id]) return;
          seen[item.product_id] = 1;
          out.push(item);
        });
        return out;
      });
    }

    function addKitItem(item, button, currency) {
      if (button.disabled) return;
      button.disabled = true;
      button.textContent = 'Adding…';
      request('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: item.variant_id, quantity: 1 }] }),
      }).then(
        function (r) {
          if (!r.ok) {
            button.disabled = false;
            button.textContent = 'Add';
            statusEl.textContent = (r.body && (r.body.description || r.body.message)) || 'Could not add ' + item.title + '.';
            return;
          }
          button.textContent = 'Added';
          button.classList.add('is-added');
          statusEl.textContent = item.title + ' added to your cart.';
          return refreshCart();
        },
        function () {
          // No response: it may or may not have landed. Re-read the cart
          // instead of offering a retry that could add it twice.
          return readCart().catch(function () { return null; }).then(function (cart) {
            if (cart && lineQuantity(cart, item.variant_id) > 0) {
              button.textContent = 'Added';
              button.classList.add('is-added');
              statusEl.textContent = item.title + ' added to your cart.';
              showCart(cart);
            } else {
              button.disabled = false;
              button.textContent = 'Add';
              statusEl.textContent = 'Could not add ' + item.title + '. Please try again.';
            }
          });
        }
      );
    }

    // --- the product form ----------------------------------------------------
    var errorEl = null;
    function showFormError(message) {
      if (!errorEl) {
        errorEl = el('p', 'cart-drawer__form-error');
        errorEl.setAttribute('role', 'alert');
        var actions = form.querySelector('.product-form__actions') || form;
        actions.insertAdjacentElement('afterend', errorEl);
      }
      errorEl.textContent = message;
      errorEl.hidden = !message;
    }

    function nativeSubmit() {
      HTMLFormElement.prototype.submit.call(form);
    }

    function openWithAdded(line, quantityAdded, cart) {
      // Everything here runs AFTER the item is in the cart: a failure may
      // leave the drawer plainer, but must never re-submit the form.
      try {
        var currency = cart ? cart.currency : 'USD';
        statusEl.textContent = '';
        if (titleEl) titleEl.textContent = 'Added to your cart';
        // T23: with the cart in hand the drawer lists every line (showCart →
        // renderLines); the single just-added row is only the fallback for a
        // cart that could not be re-read.
        if (!cart) renderAdded(line || {}, quantityAdded, currency);
        showCart(cart);
        kitEl.hidden = true;
        open();
        kitItems(cart)
          .then(function (items) { renderKit(items, currency); })
          .catch(function () { kitEl.hidden = true; });
      } catch (err) {
        window.location.assign(cartUrl);
      }
    }

    form.addEventListener('submit', function (e) {
      // Let the native post happen when the drawer cannot work.
      if (!window.fetch || !window.FormData) return;
      e.preventDefault();
      if (busy) return;
      if (addBtn && addBtn.disabled) return;

      var data = new FormData(form);
      var variantId = Number(data.get('id'));
      if (!variantId) return;
      var requested = Math.max(1, parseInt(data.get('quantity'), 10) || 1);
      var cap = qtyInput ? parseInt(qtyInput.max, 10) || DEFAULT_CAP : DEFAULT_CAP;

      busy = true;
      showFormError('');
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-busy', 'true');
        addBtn.textContent = 'Adding…';
        addBtn.setAttribute('aria-label', 'Adding to cart');
      }
      function done() {
        busy = false;
        syncButton();
      }

      getCart().then(
        function (before) {
          var already = lineQuantity(before, variantId);
          var room = cap - already;
          if (room <= 0) {
            done();
            showFormError('You already have the maximum of ' + cap + ' of this item in your cart.');
            return;
          }
          if (requested > room) {
            done();
            showFormError('You can add ' + room + ' more of this item (' + cap + ' per order).');
            return;
          }

          return request('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data }).then(
            function (r) {
              done();
              if (!r.ok) {
                showFormError((r.body && (r.body.description || r.body.message)) || 'This item could not be added to your cart.');
                return;
              }
              var line = r.body && r.body.items ? r.body.items[0] : r.body;
              return refreshCart().then(function (cart) { openWithAdded(line, requested, cart); });
            },
            function () {
              // No response to the add. Did it land? Ask the cart.
              return readCart().then(
                function (after) {
                  done();
                  if (after && lineQuantity(after, variantId) > already) {
                    var line = after.items.filter(function (i) { return i.variant_id === variantId; })[0];
                    openWithAdded(line, requested, after);
                  } else if (after) {
                    nativeSubmit();
                  } else {
                    window.location.assign(cartUrl);
                  }
                },
                function () {
                  // Cannot tell whether it landed: show the cart, never re-post.
                  done();
                  window.location.assign(cartUrl);
                }
              );
            }
          );
        },
        function () {
          // The cart could not be read before adding: nothing has been sent,
          // so the native post is safe.
          done();
          nativeSubmit();
        }
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Theme editor: a re-rendered product section brings a fresh form + drawer.
  document.addEventListener('shopify:section:load', init);
})();
